import { useCallback, useState } from "react";
import { Search } from "lucide-react";
import FilterDropdown from "../components/FilterDropdown";
import Pagination from "../components/Pagination";
import SectionHeader from "../components/SectionHeader";
import StatCard, { StatGrid } from "../components/StatCard";
import Tag from "../components/Tag";
import TextLink from "../components/TextLink";
import controlStyles from "../components/ListControls.module.css";
import tableStyles from "../components/DataTable.module.css";
import RewardDrawer from "./RewardDrawer";
import { REWARD_STAT_LABELS, REWARD_STATUS } from "./referralsData";
import useWithdrawalRequests from "../useWithdrawalRequests";
import { useDebounced } from "../useApiQuery";
import styles from "./ReferralsPage.module.css";

const PAGE_SIZE = 10;

const COLUMNS = ["Account ID", "Email", "Amount", "Status", "Action"];

const STATUS_OPTIONS = Object.entries(REWARD_STATUS).map(([value, { label }]) => ({ value, label }));

const formatUsd = (value) => `$${value.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

export default function ReferralsPage() {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState(null);
  const [page, setPage] = useState(1);
  // The open request is kept as an object: once resolved it can drop off the current
  // page (e.g. under the Requested filter) while the drawer still shows the outcome
  const [selected, setSelected] = useState(null);

  // Search runs on the API (reference, email, MT5 login, wallet, tx hash), newest first
  const debouncedQuery = useDebounced(query);
  const {
    requests: visible,
    total,
    stats,
    status: loadStatus,
    reload,
    markPaid,
    reject,
  } = useWithdrawalRequests("/api/admin/referrals", { page, pageSize: PAGE_SIZE, status, query: debouncedQuery });

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);

  const closeDrawer = useCallback(() => setSelected(null), []);
  const showResult = (result) => {
    if (result.withdrawal) {
      setSelected(result.withdrawal);
    }
    return result;
  };

  return (
    <section className={styles.page}>
      <h1 className={styles.title}>Referrals</h1>

      <section>
        <SectionHeader title="Overview" />
        <StatGrid>
          {REWARD_STAT_LABELS.map(({ key, label }) => (
            <StatCard key={key} label={label} value={stats ? formatUsd(stats[key]) : "—"} />
          ))}
        </StatGrid>
      </section>

      <section>
        <SectionHeader title="Reward payout requests" />
        <div className={`${controlStyles.toolbar} ${styles.toolbar} d-flex align-items-center justify-content-between`}>
          <FilterDropdown
            label="Status"
            options={STATUS_OPTIONS}
            value={status}
            onChange={(value) => {
              setStatus(value);
              setPage(1);
            }}
          />
          <label className={`${controlStyles.search} d-flex align-items-center`}>
            <Search size={20} strokeWidth={1.75} aria-hidden="true" />
            <input
              type="search"
              placeholder="Search"
              aria-label="Search reward requests"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
            />
          </label>
        </div>

        <div className={tableStyles.wrap}>
          <table className={`${tableStyles.table} ${tableStyles.fixed}`}>
            <thead>
              <tr>
                {COLUMNS.map((column) => (
                  <th key={column} scope="col" className={column === "Action" ? styles.alignEnd : ""}>
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((request) => {
                const requestStatus = REWARD_STATUS[request.status];

                return (
                  <tr key={request.id}>
                    <td>{request.accountId ?? "—"}</td>
                    <td>{request.email}</td>
                    <td>${request.amount.toLocaleString("en-US")}</td>
                    <td>
                      <Tag tone={requestStatus.tone}>{requestStatus.label}</Tag>
                    </td>
                    <td className={styles.alignEnd}>
                      <TextLink onClick={() => setSelected(request)}>
                        {request.status === "requested" ? "Review" : "View details"}
                      </TextLink>
                    </td>
                  </tr>
                );
              })}
              {loadStatus === "loading" ? (
                <tr>
                  <td colSpan={COLUMNS.length} className={controlStyles.empty}>
                    Loading reward requests…
                  </td>
                </tr>
              ) : loadStatus === "error" ? (
                <tr>
                  <td colSpan={COLUMNS.length} className={controlStyles.empty}>
                    We couldn't load reward requests. <TextLink arrow={false} onClick={reload}>Try again</TextLink>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} className={controlStyles.empty}>
                    {query || status ? "No reward requests match these filters." : "No reward requests yet."}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <Pagination page={currentPage} pageCount={pageCount} onChange={setPage} />
      </section>

      <RewardDrawer
        request={selected}
        onClose={closeDrawer}
        onMarkPaid={(id, txHash) => markPaid(id, txHash).then(showResult)}
        onReject={(id, reason) => reject(id, reason).then(showResult)}
      />
    </section>
  );
}
